/**
 * DOCUMENTATION:
 * Component: DesignPatternsState
 * Purpose: Educational page teaching the State and Command design patterns.
 * * * Note for Junior Developers:
 * - State: "The Mood Ring." Instead of a massive switch statement checking status
 * (if status === 'loading', if status === 'error'...), you let the State object
 * handle the behavior.
 * - Command: "The Remote Control." You wrap a method call, its arguments, and its
 * receiver into an object. This is how "Undo/Redo" functionality is built!
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsState() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "State",
            intent: "Allow an object to alter its behavior when its internal state changes. The object will appear to change its class.",
            useCase: "Traffic lights, vending machines, or complex UI components with many visual modes."
        },
        {
            name: "Command",
            intent: "Encapsulate a request as an object, thereby letting you parameterize clients with different requests, queue or log requests, and support undoable operations.",
            useCase: "Undo/Redo systems, transaction logging, or menu systems where buttons trigger complex logic."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The State Pattern (Traffic Light Logic)",
            code: `// ✅ Different states handle their own logic\nclass GreenLight {\n  sign() { return "GO"; }\n  next(light) { light.setState(new YellowLight()); }\n}\n\nclass YellowLight {\n  sign() { return "CAUTION"; }\n  next(light) { light.setState(new RedLight()); }\n}\n\nclass RedLight {\n  sign() { return "STOP"; }\n  next(light) { light.setState(new GreenLight()); }\n}\n\nclass TrafficLight {\n  constructor() { this.state = new RedLight(); }\n  setState(state) { this.state = state; }\n  change() { this.state.next(this); }\n  status() { return this.state.sign(); }\n}\n\nconst light = new TrafficLight();\nconsole.log(light.status()); // STOP\nlight.change();\nconsole.log(light.status()); // GO`,
            desc: "Without the State pattern, you'd end up with a nested 'if (status === RED) { ... } else if ...' mess. This pattern makes adding a 'Blinking Red' state trivial—you just create a new class."
        },
        {
            title: "2. The Command Pattern (Undoable Actions)",
            code: `// ✅ The Receiver (The Actual Logic)\nconst Calculator = {\n  value: 0,\n  add(amt) { this.value += amt; },\n  subtract(amt) { this.value -= amt; }\n};\n\n// ✅ The Command Object\nclass AddCommand {\n  constructor(amount) {\n    this.amount = amount;\n  }\n  execute() { Calculator.add(this.amount); }\n  undo() { Calculator.subtract(this.amount); }\n}\n\n// ✅ The Manager (Invoker)\nconst history = [];\nconst run = (cmd) => {\n  cmd.execute();\n  history.push(cmd);\n};\n\nconst undo = () => {\n  const cmd = history.pop();\n  if (cmd) cmd.undo();\n};\n\nrun(new AddCommand(10)); // Value is 10\nundo(); // Value is back to 0!`,
            desc: "The Command pattern decouples the object that *invokes* the operation from the one that *knows how to perform* it. By storing commands in a list, you gain 'Time Travel' (Undo/Redo) for free."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    STATE & COMMAND
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Managing Logic Flow
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Behavioral patterns handle the "brain" of your code. The <strong>State</strong> pattern helps objects react to internal changes without messy conditionals, while the <strong>Command</strong> pattern turns intentions into data that can be queued, logged, or reversed.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Logic Encapsulation</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
